import React from 'react';
import { Table, Pagination, TableProps, ColumnProps } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';

const { Column, HeaderCell, Cell } = Table;

export interface RsuiteColumn<T extends object>
    extends Omit<ColumnProps<T>, 'children' | 'key'> {
    key: keyof T | 'sn' | '';
    header: string;
    render?: (row: T, rowIndex?: number) => React.ReactNode;
}

interface BaseDataTableProps<T extends object> extends TableProps {
    data: T[];
    columns: RsuiteColumn<T>[];
    loading?: boolean;
    autoHeight?: boolean;
    height?: number;
    isPaginationed?: false;  // explicitly false or undefined for no pagination
}

interface PaginationProps<T extends object> extends Omit<BaseDataTableProps<T>, 'isPaginationed'> {
    isPaginationed: true;  // must be true if paginated
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    itemPerPage: number;
    totalItem: number;
    onLimitChange?: (val: number) => void;
}

type DataTableProps<T extends object> = BaseDataTableProps<T> | PaginationProps<T>;

function DataTable<T extends object>({
    data,
    columns,
    loading = false,
    autoHeight = true,
    height,
    isPaginationed = false,
    ...rest
}: DataTableProps<T>) {

    let currentPage = 1;
    let itemPerPage = data.length;
    let setCurrentPage: React.Dispatch<React.SetStateAction<number>> = () => { };
    let totalItem = data.length;
    let onLimitChange: ((val: number) => void) | undefined = undefined;
    let restTableProps = rest;

    if (isPaginationed) {
        const paginationProps = rest as PaginationProps<T>;
        currentPage = paginationProps.currentPage;
        setCurrentPage = paginationProps.setCurrentPage;
        itemPerPage = paginationProps.itemPerPage;
        totalItem = paginationProps.totalItem;
        onLimitChange = paginationProps.onLimitChange;
        restTableProps = {}; // Or any other remaining props
    }

    return (
        <>
            <Table
                data={data}
                loading={loading}
                height={autoHeight ? undefined : height}
                autoHeight={autoHeight}
                bordered
                cellBordered
                wordWrap="keep-all"
                {...rest}
            >
                {columns.map(({ header, key, render, ...rest }, index) => (
                    <Column key={String(key) || index} flexGrow={1} align="left" {...rest} >
                        <HeaderCell
                            className="text-base font-semibold text-white"
                            style={{ background: 'rgb(230, 28, 37)' }}
                        >
                            {header}
                        </HeaderCell>

                        {/*  The default Cell can be used as: <Cell dataKey={String(key)} />

                        For enhanced control over cell rendering, we use the following Cell implementation: */}

                        <Cell>
                            {(rowData: T, rowIndex?: number) =>
                                render
                                    ? render(rowData, rowIndex)
                                    : key === 'sn'
                                        ? (rowIndex ?? 0) + 1 + (isPaginationed ? (currentPage - 1) * itemPerPage : 0)
                                        : (rowData)[String(key)] || 'N/A'
                            }
                        </Cell>
                    </Column>
                ))}
            </Table>

            {isPaginationed && (
                <div className="flex justify-end my-2">
                    <Pagination
                        prev
                        next
                        first
                        last
                        ellipsis
                        boundaryLinks
                        maxButtons={5}
                        size="sm"
                        layout={['total', '-', 'limit', '|', 'pager', 'skip']}
                        total={totalItem}
                        limitOptions={[10, 30, 50]}
                        limit={itemPerPage}
                        activePage={currentPage}
                        onChangePage={setCurrentPage}
                        onChangeLimit={(val) => {
                            onLimitChange?.(val);
                            setCurrentPage(1);
                        }}
                    />
                </div>
            )}
        </>
    );
}

export default DataTable;
